import { useEffect, useState } from "react";
import { ReactComponent as AmexLogo } from "@/assets/icons/amex-color.svg";
import { ReactComponent as DinersLogo } from "@/assets/icons/diners-color.svg";
import { ReactComponent as DiscoverLogo } from "@/assets/icons/discover-color.svg";
import { ReactComponent as JcbLogo } from "@/assets/icons/jcb-color.svg";
import { ReactComponent as MastercardLogo } from "@/assets/icons/mastercard-color.svg";
import { ReactComponent as UnionPayLogo } from "@/assets/icons/unionpay-color.svg";
import { ReactComponent as VisaLogo } from "@/assets/icons/visa-color.svg";
import { Button, Icon, Loading, Typography } from "@/components/atoms";
import { CardFormModal } from "@/components/organisms/PaymentSettings/components/CardFormModal";
import {
  MAX_CARDS_PER_ACCOUNT,
  getCardBrandLogo,
  getCardExpiry,
  getCardLastFour,
  getCardNicknameDisplay,
  isCreditCard,
} from "@/components/organisms/PaymentSettings/helpers";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGuestBooking } from "@/components/pages/Guest/GuestBookingContext";
import {
  clientPaymentApi,
  useGetClientPaymentMethodsQuery,
  useLazyGetClientPaymentMethodQuery,
} from "@/redux/apis/clientPayment/clientPaymentApi";
import { ClientPaymentMethod, PaymentMethodType } from "@/redux/apis/clientPayment/types";
import { useGetClientProfileQuery } from "@/redux/apis/clientProfile/clientProfileApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { accountIdSelector } from "@/redux/slices/account/selectors";
import { setPaymentMethod } from "@/redux/slices/clientBooking/clientBookingSlice";
import { selectPaymentMethod } from "@/redux/slices/clientBooking/selectors";
import { configSelector } from "@/redux/slices/config/selectors";
import { addToast, clsx } from "@/utils";
import { useBookingViewOnly } from "./BookingViewOnlyContext";
import { GuestPaymentPanel } from "./GuestPaymentPanel";
import { ViewOnlyFieldset } from "./ViewOnlyFieldset";

interface PaymentPanelProps {
  className?: string;
  /** Return to the Trip Details step. */
  onPrevious: () => void;
  /**
   * Open the Booking Summary slide-in (below lg the summary isn't a sidebar, and Book Trip
   * lives inside it) — renders the footer's primary "View Summary" button when provided.
   */
  onViewSummary?: () => void;
}

// Accepted card networks shown in the "safe & secure checkout" trust badge, in Figma order.
// Presentational only — Stripe's PaymentElement renders its own accepted-brand row inside the
// add-card modal; this badge just reassures before the card form is opened.
const CARD_BRANDS = [
  { name: "Visa", Logo: VisaLogo },
  { name: "Mastercard", Logo: MastercardLogo },
  { name: "American Express", Logo: AmexLogo },
  { name: "JCB", Logo: JcbLogo },
  { name: "Discover", Logo: DiscoverLogo },
  { name: "Diners Club", Logo: DinersLogo },
  { name: "UnionPay", Logo: UnionPayLogo },
];

const SectionHeader = ({ icon, label }: { icon: string; label: string }) => (
  <div className="flex items-center gap-2">
    <Icon name={icon} size="md" className="text-neutral-black" />
    <Typography variant="action" className="text-neutral-black">
      {label}
    </Typography>
  </div>
);

const rowClass = (selected: boolean) =>
  clsx(
    "flex w-full items-center gap-3 rounded-lg border px-2.5 py-1.5 text-left",
    selected ? "border-primary bg-primary-light" : "border-neutral-mid-gray bg-neutral-surface-gray"
  );

const IconTile = ({ children }: React.PropsWithChildren) => (
  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-neutral-mid-gray bg-white">{children}</span>
);

const DefaultBadge = () => (
  <Typography variant="paragraph" className="shrink-0 text-vivid-purple-dark">
    (Default)
  </Typography>
);

const CardMethodRow = ({
  method,
  selected,
  onSelect,
}: {
  method: ClientPaymentMethod;
  selected: boolean;
  onSelect: (method: ClientPaymentMethod) => void;
}) => {
  const textColor = selected ? "text-primary-dark" : "text-neutral-dark-gray";
  const expiry = getCardExpiry(method.card_data);
  return (
    <button type="button" role="radio" aria-checked={selected} onClick={() => onSelect(method)} className={rowClass(selected)}>
      <span className="flex min-w-0 flex-1 items-center gap-3">
        <IconTile>{getCardBrandLogo(method.card_data?.issuer)}</IconTile>
        <span className="flex min-w-0 items-center gap-1.5">
          <Typography variant="action" className={clsx("truncate", textColor)}>
            {getCardNicknameDisplay(method)}
          </Typography>
          {method.default && <DefaultBadge />}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end">
        <Typography variant="action" className={textColor}>
          <span aria-hidden className="mr-1 tracking-[4px]">
            ••••
          </span>
          {getCardLastFour(method.card_data)}
        </Typography>
        {expiry && (
          <Typography variant="paragraph" className={textColor}>
            Expires {expiry}
          </Typography>
        )}
      </span>
    </button>
  );
};

const InvoiceMethodRow = ({
  method,
  selected,
  companyName,
  onSelect,
}: {
  method: ClientPaymentMethod;
  selected: boolean;
  companyName?: string;
  onSelect: (method: ClientPaymentMethod) => void;
}) => {
  const textColor = selected ? "text-primary-dark" : "text-neutral-dark-gray";
  return (
    <button type="button" role="radio" aria-checked={selected} onClick={() => onSelect(method)} className={rowClass(selected)}>
      <IconTile>
        <Icon name="DocumentText" size={18} className="text-neutral-dark-gray" />
      </IconTile>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-1.5">
          <Typography variant="action" className={textColor}>
            Company Invoicing
          </Typography>
          {method.default && <DefaultBadge />}
        </span>
        {companyName && (
          <Typography variant="small" className={clsx("!font-medium", textColor)}>
            Billed to: {companyName}
          </Typography>
        )}
      </span>
    </button>
  );
};

/**
 * The "Payment" panel — step 3 of the client booking flow (DEV-448). Lists the account's saved
 * payment methods (Company Invoicing first when present, then credit cards) as radio rows writing
 * the selection into clientBookingSlice. "Add credit card" reuses the settings CardFormModal and
 * auto-selects the newly saved card for this booking. Selection here is per-booking only — it never
 * changes the account default.
 */
const AuthedPaymentPanel = ({ className, onPrevious, onViewSummary }: PaymentPanelProps) => {
  const dispatch = useAppDispatch();
  const viewOnly = useBookingViewOnly();
  // Operator OBO: the client's wallet, Stripe customer (add-card) and invoice profile —
  // never the operator's own. Self-service falls back to the active account, unchanged.
  const { accountId: scopedAccountId } = useClientScope();
  const activeAccountId = useAppSelector(accountIdSelector);
  const accountId = scopedAccountId ?? activeAccountId;
  const config = useAppSelector(configSelector);
  const selectedMethod = useAppSelector(selectPaymentMethod);
  const [addCardOpen, setAddCardOpen] = useState(false);

  const { data: paymentMethods = [], isLoading, isError, refetch } = useGetClientPaymentMethodsQuery(accountId, { skip: !accountId });
  const [getPaymentMethod] = useLazyGetClientPaymentMethodQuery();

  const invoiceMethod = paymentMethods.find((m) => m.type === PaymentMethodType.INVOICE);
  const creditCards = paymentMethods.filter(isCreditCard);

  // Company the invoice bills to — same profile query the rest of the client portal reads,
  // scoped to the managed client under operator OBO.
  const { data: profile } = useGetClientProfileQuery(scopedAccountId ? { accountId: scopedAccountId } : undefined, {
    skip: !invoiceMethod,
  });
  const companyName = profile?.company?.company_name;

  const operatorName = (config?.name || "your operator").replace(/\.+$/, "");
  const termsUrl = config?.urls?.terms;

  // Preselect the account default whenever nothing is chosen yet. This covers a case the slice's
  // fulfilled-action matcher misses: a re-book ("Book a Return Trip") resets the draft to
  // paymentMethod:null, but the payment methods are already cached from the first booking — so no new
  // fetch fires, the matcher never re-runs, and the default would otherwise stay unselected. Reads the
  // panel's already-scoped query result, so it never preselects another client's card under OBO.
  // Skips view-only (a finished booking keeps its own method) and never overrides an explicit choice.
  useEffect(() => {
    if (viewOnly || selectedMethod || !paymentMethods.length) return;
    // Prefer the account default; fall back to the first available method so the payment step never opens
    // with nothing selected. Accounts whose methods carry no `default` flag (e.g. an invoice-only client,
    // or cards where none is flagged) previously left the selection blank — the designer's report.
    const preselect = paymentMethods.find((method) => method.default) ?? paymentMethods[0];
    dispatch(setPaymentMethod(preselect));
  }, [viewOnly, selectedMethod, paymentMethods, dispatch]);

  const handleSelect = (method: ClientPaymentMethod) => dispatch(setPaymentMethod(method));

  const handleCardSaved = async (newPaymentMethodId: string) => {
    setAddCardOpen(false);
    addToast("success", "Successfully added new credit card");
    try {
      // CardFormModal hands back the persisted uuid, or a SetupIntent id when its webhook poll
      // couldn't resolve one — getClientPaymentMethod accepts either and returns the full method.
      const saved = await getPaymentMethod({ accountId, paymentMethodId: newPaymentMethodId }).unwrap();
      if (saved?.uuid) dispatch(setPaymentMethod(saved));
    } catch {
      // Card was added but couldn't be fetched for auto-select — it stays pickable from the refreshed list.
    }
    dispatch(clientPaymentApi.util.invalidateTags(["ClientPaymentMethods"]));
  };

  return (
    <div
      className={clsx(
        // Mobile: full-bleed white page. md+: floating card over the map (matches TripDetailsPanel).
        "flex flex-col bg-white md:rounded-2xl md:border md:border-neutral-mid-gray md:shadow-lg",
        className
      )}
    >
      <ViewOnlyFieldset>
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-5">
        <div>
          <Typography variant="h2">Payment</Typography>
          <Typography variant="muted" className="mt-1 block text-neutral-dark-gray">
            Select a credit card to complete your booking
          </Typography>
        </div>

        {isLoading ? (
          <div className="relative min-h-[120px]">
            <Loading />
          </div>
        ) : isError ? (
          // A failed fetch must NOT look like "no saved cards" (which invites adding a duplicate and
          // still blocks booking). Show the real problem + a retry.
          <div className="flex flex-col items-start gap-3">
            <Typography variant="paragraph" className="text-danger">
              We couldn&apos;t load your payment methods. Please try again.
            </Typography>
            <Button variant="tertiary" size="md" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <div role="radiogroup" aria-label="Payment method" className="flex flex-col gap-6">
            {invoiceMethod && (
              <div className="flex flex-col gap-3">
                <SectionHeader icon="DocumentText" label="Invoicing" />
                <div className="flex flex-col gap-1">
                  <InvoiceMethodRow
                    method={invoiceMethod}
                    selected={selectedMethod?.uuid === invoiceMethod.uuid}
                    companyName={companyName}
                    onSelect={handleSelect}
                  />
                  <Typography variant="small" className="!font-medium text-neutral-silver-gray">
                    Invoicing is set up by your company. To change billing terms, contact {operatorName}.
                  </Typography>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <SectionHeader icon="Card" label="Credit Card Payments" />
              <div className="flex flex-col gap-2">
                {creditCards.map((method) => (
                  <CardMethodRow
                    key={method.uuid}
                    method={method}
                    selected={selectedMethod?.uuid === method.uuid}
                    onSelect={handleSelect}
                  />
                ))}
                {paymentMethods.length === 0 && (
                  <Typography variant="paragraph" className="text-neutral-dark-gray">
                    You don&apos;t have any saved payment methods yet. Add a credit card to complete your booking.
                  </Typography>
                )}
                {!viewOnly && creditCards.length < MAX_CARDS_PER_ACCOUNT && (
                  <button
                    type="button"
                    onClick={() => setAddCardOpen(true)}
                    className="flex h-[52px] w-full items-center gap-2 rounded-lg border border-dashed border-info bg-info-light px-3.5"
                  >
                    <Icon name="Add" size="lg" className="shrink-0 text-info-dark" />
                    <Typography variant="action" className="text-info-dark">
                      Add credit card
                    </Typography>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Pinned to the bottom of the scroll area, above the footer — matches the Figma layout. */}
        <div className="mt-auto flex flex-col gap-3">
          <div className="flex flex-col gap-3 rounded-lg border border-info bg-info-light px-2 py-1.5">
            <Typography variant="small" className="!font-medium text-info-dark">
              Your card will be charged after your trip is completed, however some operators reserve the right to pre-charge on select
              trips.
            </Typography>
            <Typography variant="small" className="!font-medium text-info-dark">
              You can view, edit or cancel your booking at any time in accordance with {operatorName}&apos;s{" "}
              {termsUrl ? (
                <a href={termsUrl} target="_blank" rel="noreferrer" className="underline">
                  Terms and Conditions
                </a>
              ) : (
                <span className="underline">Terms and Conditions</span>
              )}
              .
            </Typography>
          </div>

          {/* "Guaranteed safe & secure checkout" trust badge — composed from per-brand SVGs (was a
              flat 2x PNG). The Powered-by-Stripe pill + accepted-network row are presentational. */}
          <div className="rounded-lg border border-neutral-mid-gray bg-white p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-1.5">
                <Icon name="Lock1" variant="Bold" size={16} className="shrink-0 text-neutral-black" />
                <Typography variant="small" className="!font-medium text-neutral-dark-gray">
                  Guaranteed <span className="font-bold text-neutral-black">safe &amp; secure</span> checkout
                </Typography>
              </div>
              <span className="flex shrink-0 items-center gap-1 rounded bg-neutral-black px-2 py-1">
                <span className="text-[10px] leading-none text-white/60">Powered by</span>
                <span className="text-[13px] font-bold leading-none text-white">stripe</span>
              </span>
            </div>
            <hr className="my-2.5 border-neutral-mid-gray" />
            <ul className="flex items-center gap-1.5">
              {CARD_BRANDS.map(({ name, Logo }) => (
                <li key={name} className="flex h-7 flex-1 items-center justify-center rounded border border-neutral-mid-gray">
                  <Logo role="img" aria-label={name} className="h-4 w-auto max-w-[85%]" />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      </ViewOnlyFieldset>

      {/* Fixed at the bottom. Divider on mobile only (md+ card has no footer divider) — matches TripDetailsPanel.
          Below lg the summary is a slide-in (not a sidebar) and holds Book Trip, so the primary action here
          is "View Summary" (per the mobile Figma). */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-neutral-mid-gray p-4 md:border-t-0">
        <Button variant="secondary" size="md" onClick={onPrevious}>
          Previous Step
        </Button>
        {onViewSummary && (
          <Button variant="primary" size="md" className="lg:hidden" onClick={onViewSummary}>
            View Summary
          </Button>
        )}
      </div>

      <CardFormModal open={addCardOpen} mode="add" accountId={accountId} onClose={() => setAddCardOpen(false)} onSaved={handleCardSaved} />
    </div>
  );
};

/**
 * Payment step (step 3). Branches on the guest seam only: the authed path (`AuthedPaymentPanel`) is
 * unchanged and still reads the account's saved methods; the guest flow captures a card client-side
 * (`GuestPaymentPanel`) and never reads saved payment methods.
 */
export const PaymentPanel = (props: PaymentPanelProps) => {
  const { isGuest } = useGuestBooking();
  return isGuest ? <GuestPaymentPanel {...props} /> : <AuthedPaymentPanel {...props} />;
};
